import { MtlsCertificate } from '../models/MtlsCertificate';
import { METHOD, RequestStatus } from 'shared/Requests';
import { CertificateInputs, UploadCertificateResponse } from 'interfaces';
import { BlipApi } from './BlipApi';
import axios from 'axios';

export default class MtlsCertificateApi {
  private readonly POSTMASTERMTLS = 'postmaster@mtls.blip.ai';
  private code = '';
  private status = '';

  public getStatus() {
    return this.status;
  }

  public getCode() {
    return this.code;
  }

  public setSuccess() {
    this.status = RequestStatus.SUCCESS;
  }

  public setFailure() {
    this.status = RequestStatus.FAILURE;
  }

  public setErrorLog(e) {
    const error = JSON.parse(<string>e);
    const {
      status,
      reason: { code },
    } = JSON.parse(error.message);
    this.code = code;
    this.status = status;
    console.log(e);
  }

  public async deleteMtlsCertificate(tenantId: string, id: string) {
    const uri = `/certificate-mtls/${tenantId}/${id}`;

    const command = {
      to: this.POSTMASTERMTLS,
      method: METHOD.DELETE,
      uri: uri,
    };

    try {
      await BlipApi.sendCommand(command);
      this.setSuccess();
    } catch (e) {
      this.setErrorLog(e);
    }
  }

  public async deleteHost(tenantId: string, id: string) {
    const uri = `/certificate-mtls/${tenantId}/host/${id}`;

    const command = {
      to: this.POSTMASTERMTLS,
      method: METHOD.DELETE,
      uri: uri,
    };

    try {
      await BlipApi.sendCommand(command);
      this.setSuccess();
    } catch (e) {
      this.setErrorLog(e);
    }
  }

  public async getMtlsCertificates(tenantId: string): Promise<MtlsCertificate[]> {
    const uri = `/certificates-mtls/${tenantId}`;

    const command = {
      to: this.POSTMASTERMTLS,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const {
        response: { items },
      } = await BlipApi.sendCommand(command);

      this.setSuccess();
      return items.map((item) => new MtlsCertificate(item));
    } catch (e) {
      this.setErrorLog(e);
      return new Array<MtlsCertificate>();
    }
  }

  public async getUrlUploadCertificate(tenantId: string): Promise<string> {
    const uri = `/upload-certificate-uri/${tenantId}`;

    const command = {
      to: this.POSTMASTERMTLS,
      method: METHOD.GET,
      uri: uri,
    };

    try {
      const { response } = await BlipApi.sendCommand(command);
      this.setSuccess();
      return response;
    } catch (e) {
      this.setErrorLog(e);
      return '';
    }
  }

  public async uploadCertificate(
    certificateInputs: CertificateInputs,
    tenantId: string,
  ): Promise<UploadCertificateResponse> {
    const uri = await this.getUrlUploadCertificate(tenantId);
    if (!uri) {
      throw new Error('Error getting upload certificate uri');
    }

    const formData = new FormData();
    formData.append('password', certificateInputs.password);
    formData.append('file', new Blob([certificateInputs.file]));

    try {
      const {
        data: { storage_id, name, contract_id },
      } = await axios({
        url: uri,
        method: METHOD.POST,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      this.setSuccess();
      return { storage_id, name, contract_id };
    } catch (e) {
      console.log(e);
      this.setFailure();
      return { storage_id: '', name: '', contract_id: '' };
    }
  }

  public async setMtlsCertificate(certificateInputs: CertificateInputs, tenantId: string): Promise<string> {
    const response = await this.uploadCertificate(certificateInputs, tenantId);
    if (!response) {
      throw new Error('Error uploading certificate');
    }
    const commandType = 'application/vnd.iris.mtls.certificate-info+json';
    const uri = '/certificates-mtls';

    const command = {
      to: this.POSTMASTERMTLS,
      method: METHOD.SET,
      uri: uri,
      type: commandType,
      resource: {
        certificate_id: `${response.storage_id}:${tenantId}-${response.name}`,
        description: certificateInputs.description,
        hosts: certificateInputs.hosts.map((host) => host.host),
      },
    };

    try {
      const response = await BlipApi.sendCommand(command);
      this.setSuccess();
      return response;
    } catch (e) {
      this.setErrorLog(e);
      return '';
    }
  }
}
